import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  User,
  Car,
  Package,
  Wrench,
  ShieldCheck,
  Clock,
  ArrowRight,
  ArrowLeft,
  X,
  Building,
  UserPlus,
  CheckCircle2,
  Sparkles,
  FileText,
  Hash,
  Check,
  RotateCcw,
  Lock,
  ShieldAlert,
  Copy,
  KeyRound,
  AlertTriangle,
  Info,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  FormField,
  TextInput,
  FileUpload,
  Checkbox,
  Button,
  SearchInput,
  StatusBadge,
} from "../../../components/ui";
import styles from "./PublicVisitorCheckin.module.css";

const ENTRY_TYPES = [
  {
    value: "VISITOR",
    title: "General Visitor / Guest",
    subtitle: "Visiting on foot for an official meeting, interview, or personal visit",
    icon: <User size={22} />,
    badge: "Most Common",
    badgeType: "primary",
  },
  {
    value: "VEHICLE",
    title: "Vehicle Entry (Cab / Personal)",
    subtitle: "Entering by 2-wheeler, car, auto, or cab drop / pickup",
    icon: <Car size={22} />,
    badge: "Gate 1 & 2",
    badgeType: "warning",
  },
  {
    value: "DELIVERY",
    title: "Delivery Partner",
    subtitle: "Dropping food, grocery, parcel, or courier (Swiggy, Zomato, Amazon, Blinkit)",
    icon: <Package size={22} />,
    badge: "Fast Track",
    badgeType: "success",
  },
  {
    value: "VENDOR",
    title: "Service & Maintenance",
    subtitle: "Contractor, technician, maintenance staff, or vendor work",
    icon: <Wrench size={22} />,
    badge: "Work Pass",
    badgeType: "neutral",
  },
];

const DELIVERY_COMPANIES = ["Zomato", "Swiggy", "Amazon", "Blinkit", "Zepto", "Flipkart", "BlueDart", "Delhivery"];
const CAB_COMPANIES = ["Personal Vehicle", "Uber", "Ola", "Rapido", "BluSmart"];
const VISITOR_VEHICLE_TYPES = ["Personal Bike", "Personal Car", "Auto / Cab", "Commercial"];

const PURPOSE_BY_TYPE = {
  VISITOR: [
    "Official Meeting",
    "Job Interview",
    "Personal Visit",
    "Document Submission",
    "Other",
  ],
  VEHICLE: [
    "Official Meeting",
    "Job Interview",
    "Personal Visit",
    "Cab Drop / Pickup",
    "Document Submission",
    "Other",
  ],
  DELIVERY: [], // Hidden in Step 3, auto-set to "Delivery / Courier Drop"
  VENDOR: [
    "Maintenance & Repair",
    "Vendor Work",
    "Electrical / Plumbing",
    "Inspection & Audit",
    "Document Submission",
    "Other",
  ],
};

const STEP_2_HEADINGS = {
  VISITOR: {
    title: "Your Basic Details",
    sub: "Please enter your name, verify mobile number, and take a quick security photo.",
    nameLabel: "Your Full Name",
    namePlaceholder: "Enter full name as per Govt ID",
  },
  VEHICLE: {
    title: "Driver & Vehicle Details",
    sub: "Please provide driver identity and vehicle registration details for gate access.",
    nameLabel: "Driver / Visitor Full Name",
    namePlaceholder: "Enter driver or visitor full name",
  },
  DELIVERY: {
    title: "Delivery Partner Details",
    sub: "Quick mobile verification and delivery details for fast gate clearance.",
    nameLabel: "Delivery Agent Name",
    namePlaceholder: "Enter your name as registered with delivery app",
  },
  VENDOR: {
    title: "Service Personnel Details",
    sub: "Please enter your name, verify mobile number, and take a photo for work pass.",
    nameLabel: "Technician / Contractor Name",
    namePlaceholder: "Enter technician or supervisor full name",
  },
};

const STEP_3_HEADINGS = {
  VISITOR: {
    title: "Visit Information",
    sub: "Whom are you visiting today, total persons in your group, and purpose of visit.",
  },
  VEHICLE: {
    title: "Visit Information",
    sub: "Whom are you visiting today, total persons in vehicle, and purpose of visit.",
  },
  DELIVERY: {
    title: "Delivery Destination",
    sub: "Select the employee, department, flat, or wing for this delivery drop.",
  },
  VENDOR: {
    title: "Work Assignment Details",
    sub: "Select the host employee or department where service/maintenance will be done.",
  },
};

export default function PublicVisitorCheckin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const draftParam = searchParams.get("draft") || "";
  const rawSocietyId =
    searchParams.get("societyId") ||
    searchParams.get("society_id") ||
    searchParams.get("property_id") ||
    searchParams.get("propertyId") ||
    "115";
  const societyId = rawSocietyId === "114" ? "115" : rawSocietyId;
  const gateParam = searchParams.get("gate") || "1";
  const typeParam = (searchParams.get("type") || searchParams.get("category") || "").toUpperCase();

  const mapTypeParam = (val) => {
    if (val === "PEDESTRIAN" || val === "VISITOR") return "VISITOR";
    if (val === "VEHICLE") return "VEHICLE";
    if (val === "DELIVERY") return "DELIVERY";
    if (val === "VENDOR" || val === "STAFF") return "VENDOR";
    return null;
  };
  const resolvedType = mapTypeParam(typeParam);

  // Stepper & Flow State (Step 1: Entry Type -> Step 2: Basic Details -> Step 3: Visit Details)
  const [currentStep, setCurrentStep] = useState(1);
  const [hasRestoredDraft, setHasRestoredDraft] = useState(false);

  // Visit Type: VISITOR | VEHICLE | DELIVERY | VENDOR
  const [visitType, setVisitType] = useState(resolvedType || "VISITOR");

  // Form State
  const [fullName, setFullName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");

  // Indian HSRP Vehicle Number Plate Box State
  const [plateState, setPlateState] = useState("");
  const [plateRto, setPlateRto] = useState("");
  const [plateSeries, setPlateSeries] = useState("");
  const [plateNumber, setPlateNumber] = useState("");
  const [useSinglePlateInput, setUseSinglePlateInput] = useState(false);

  const plateStateRef = useRef(null);
  const plateRtoRef = useRef(null);
  const plateSeriesRef = useRef(null);
  const plateNumberRef = useRef(null);
  const isSyncingPlateRef = useRef(false);

  const [departmentId, setDepartmentId] = useState("");
  const [hostUserId, setHostUserId] = useState("");
  const [hostSearchQuery, setHostSearchQuery] = useState("");
  const [selectedHostName, setSelectedHostName] = useState("");
  const [selectedHostObj, setSelectedHostObj] = useState(null);
  const [destinationMode, setDestinationMode] = useState("EMPLOYEE"); // "EMPLOYEE" | "DEPARTMENT"
  const [deptSearchQuery, setDeptSearchQuery] = useState("");
  const [selectedDeptObj, setSelectedDeptObj] = useState(null);
  const [purpose, setPurpose] = useState("");
  const [noOfVisitors, setNoOfVisitors] = useState(1);
  const [deliveryCompany, setDeliveryCompany] = useState("");
  const [orderId, setOrderId] = useState("");
  const [dpdpConsent, setDpdpConsent] = useState(true);

  // OTP State - 6 Individual Box Digits
  const [otpSent, setOtpSent] = useState(false);
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpToken, setOtpToken] = useState("");
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const otpInputRefs = useRef([]);

  // Returning Visitor Auto-Fill State
  const [isReturningVisitor, setIsReturningVisitor] = useState(false);
  const [returningData, setReturningData] = useState(null);
  const [showPtmChanger, setShowPtmChanger] = useState(false);

  // Draft State (for Flow B parking gate)
  const [isDraftLocked, setIsDraftLocked] = useState(false);
  const [draftCountdown, setDraftCountdown] = useState(null);

  // Data State
  const [departments, setDepartments] = useState([]);
  const [departmentEmployees, setDepartmentEmployees] = useState([]);
  const [loadingDeptEmployees, setLoadingDeptEmployees] = useState(false);
  const [hostResults, setHostResults] = useState([]);
  const [searchingHosts, setSearchingHosts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [propertyName, setPropertyName] = useState("");

  // Live Camera Selfie State
  const [selfieDataUrl, setSelfieDataUrl] = useState(null);

  // Active Visit Detection State (For QR Re-scan when visitor is in active state or checked-in)
  const [activeVisit, setActiveVisit] = useState(null);
  const [showActivePrompt, setShowActivePrompt] = useState(false);
  const [showMeetAnotherModal, setShowMeetAnotherModal] = useState(false);
  const [meetAnotherQuery, setMeetAnotherQuery] = useState("");
  const [meetAnotherResults, setMeetAnotherResults] = useState([]);
  const [searchingMeetAnother, setSearchingMeetAnother] = useState(false);
  const [selectedNewHost, setSelectedNewHost] = useState(null);
  const [submittingMeetAnother, setSubmittingMeetAnother] = useState(false);
  const [copiedPassCode, setCopiedPassCode] = useState(false);
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const deviceUnlockedRef = useRef(false);

  // Check local device storage for active ongoing visit on mount
  useEffect(() => {
    const checkStoredActivePass = async () => {
      try {
        const storedStr = localStorage.getItem("asmita_active_pass");
        if (!storedStr) return;
        const stored = JSON.parse(storedStr);
        const code = stored?.status_token || stored?.pass_code || stored?.request_number;
        if (!code) return;

        // Instant Hydration: Show Device Locked overlay immediately without waiting for network!
        setActiveVisit({
          pass_code: stored.pass_code || code,
          request_number: stored.request_number || code,
          status_token: code,
          status: stored.status || "ACTIVE",
          visitor_name: stored.visitor_name || "Visitor",
          visitor_phone: stored.phone || "",
          host_name: stored.host_name || "Department Team",
          department_name: stored.department_name || "General Facility",
          cabin_code: stored.cabin_code || "Main Desk",
          desk_code: stored.desk_code || "Desk 1",
          floor_number: stored.floor_number ?? "Ground",
          is_checked_in: stored.status === "CHECKED_IN",
          _isLiveUpdating: true,
        });
        setShowActivePrompt(true);

        const res = await fetch(`/api/public/visitor/status/${code}`);
        const data = await res.json();
        if (data.success) {
          const disallowed = ["CHECKED_OUT", "CANCELLED", "REVOKED", "EXPIRED", "REJECTED"];
          const isDone = disallowed.includes(data.status) || data.is_checked_out || data.is_rejected || data.is_expired;
          if (!isDone) {
            setActiveVisit({
              ...data,
              pass_code: data.pass_code || code,
              request_number: data.request_number || code,
              status_token: code,
              status: data.status,
              visitor_name: data.visitor_name || stored.visitor_name,
              host_name: data.host_name || stored.host_name,
              department_name: data.department_name || stored.department_name,
              cabin_code: data.cabin_code || stored.cabin_code,
              desk_code: data.desk_code || stored.desk_code,
              floor_number: data.floor_number ?? stored.floor_number,
              is_checked_in: data.is_checked_in || data.status === "CHECKED_IN",
              _isLiveUpdating: false,
            });
            setShowActivePrompt(true);
          } else {
            localStorage.removeItem("asmita_active_pass");
            setShowActivePrompt(false);
            setActiveVisit(null);
          }
        }
      } catch (err) {
        console.warn("Storage active pass lookup error:", err);
      }
    };
    checkStoredActivePass();
  }, []);

  // Check active pass when visitor types 10-digit mobile number
  useEffect(() => {
    if (!mobileNumber || mobileNumber.length !== 10) return;
    if (deviceUnlockedRef.current) return;
    const checkActiveByPhone = async () => {
      try {
        const res = await fetch(`/api/public/visitor/active-pass?phone=${mobileNumber}&society_id=${societyId}`);
        const data = await res.json();
        if (data.success && data.has_active_pass && data.pass) {
          setActiveVisit(data.pass);
          setShowActivePrompt(true);
          try {
            localStorage.setItem("asmita_active_pass", JSON.stringify(data.pass));
          } catch (err) {
            // Ignore localStorage errors
          }
        }
      } catch (err) {
        console.warn("Active pass phone lookup error:", err);
      }
    };
    checkActiveByPhone();
  }, [mobileNumber, societyId]);

  // Search candidate hosts for "Meet Another Person"
  useEffect(() => {
    if (!showMeetAnotherModal) return;
    const timer = setTimeout(async () => {
      setSearchingMeetAnother(true);
      try {
        const q = meetAnotherQuery.trim();
        const res = await fetch(`/api/public/visitor/hosts?query=${encodeURIComponent(q || "")}&society_id=${societyId}&mode=name`);
        const json = await res.json();
        if (json.success) {
          const raw = json.hosts || json.data || [];
          if (q) {
            const lower = q.toLowerCase();
            setMeetAnotherResults(raw.filter((h) => (h.full_name || h.name || "").toLowerCase().includes(lower)));
          } else {
            setMeetAnotherResults(raw.slice(0, 15));
          }
        }
      } catch (err) {
        console.error("Error searching candidate hosts:", err);
      } finally {
        setSearchingMeetAnother(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [showMeetAnotherModal, meetAnotherQuery, societyId]);

  const handleConfirmMeetAnother = async () => {
    if (!selectedNewHost) {
      toast.warn("Please select the person you would like to meet");
      return;
    }
    const code = activeVisit?.status_token || activeVisit?.pass_code || activeVisit?.request_number;
    if (!code) {
      toast.error("Active pass reference missing. Please try scanning again.");
      return;
    }

    setSubmittingMeetAnother(true);
    try {
      const res = await fetch(`/api/public/visitor/status/${code}/update-ptm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          assigned_user_id: selectedNewHost.user_id,
          department_id: selectedNewHost.department_id,
          cabin_code: selectedNewHost.cabin_code,
          desk_code: selectedNewHost.desk_code,
        }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        toast.success(
          json.is_checked_in
            ? "Person to meet changed and notified."
            : `Updated to meet ${selectedNewHost.full_name}.`
        );
        setShowMeetAnotherModal(false);
        setShowActivePrompt(false);
        navigate(`/visitor/status/${code}`, { replace: true });
      } else {
        toast.error(json.error || json.message || "Could not update person to meet");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmittingMeetAnother(false);
    }
  };

  // 1. Load Draft if present in URL
  useEffect(() => {
    if (!draftParam) return;
    const fetchDraft = async () => {
      try {
        const res = await fetch(`/api/public/visitor/draft/${draftParam}`);
        const data = await res.json();
        if (data.success && data.draft) {
          setVisitType("VEHICLE");
          setFullName(data.draft.visitor_name || "");
          setVehicleNumber(data.draft.vehicle_number || "");
          setIsDraftLocked(true);
          setDraftCountdown(data.draft.seconds_remaining);
          toast.info("Vehicle details pre-filled from Parking Gate guard");
        } else if (data.error) {
          toast.error(data.error);
        }
      } catch (err) {
        console.error("Draft fetch error:", err);
      }
    };
    fetchDraft();
  }, [draftParam]);

  // Draft countdown timer
  useEffect(() => {
    if (draftCountdown == null || draftCountdown <= 0) return;
    const timer = setInterval(() => {
      setDraftCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [draftCountdown]);

  // Auto-Save: Restore draft on component mount
  useEffect(() => {
    if (draftParam) return;
    try {
      const raw = localStorage.getItem("asmita_visitor_checkin_draft_v2");
      if (!raw) return;
      const draft = JSON.parse(raw);
      const ONE_DAY_MS = 24 * 60 * 60 * 1000;
      if (draft.savedAt && Date.now() - draft.savedAt > ONE_DAY_MS) {
        localStorage.removeItem("asmita_visitor_checkin_draft_v2");
        return;
      }
      if (draft.visitType) setVisitType(draft.visitType);
      if (draft.fullName) setFullName(draft.fullName);
      if (draft.mobileNumber) setMobileNumber(draft.mobileNumber);
      if (draft.isOtpVerified) setIsOtpVerified(draft.isOtpVerified);
      if (draft.otpToken) setOtpToken(draft.otpToken);
      if (draft.selfieDataUrl) setSelfieDataUrl(draft.selfieDataUrl);
      if (draft.deliveryCompany) setDeliveryCompany(draft.deliveryCompany);
      if (draft.orderId) setOrderId(draft.orderId);
      if (draft.vehicleNumber) setVehicleNumber(draft.vehicleNumber);
      if (draft.destinationMode) setDestinationMode(draft.destinationMode);
      if (draft.departmentId) setDepartmentId(draft.departmentId);
      if (draft.selectedDeptObj) setSelectedDeptObj(draft.selectedDeptObj);
      if (draft.hostUserId) setHostUserId(draft.hostUserId);
      if (draft.selectedHostName) setSelectedHostName(draft.selectedHostName);
      if (draft.selectedHostObj) setSelectedHostObj(draft.selectedHostObj);
      if (draft.purpose) setPurpose(draft.purpose);
      if (draft.noOfVisitors) setNoOfVisitors(draft.noOfVisitors);
      if (draft.dpdpConsent !== undefined) setDpdpConsent(draft.dpdpConsent);
      if (draft.currentStep) setCurrentStep(draft.currentStep);
      setHasRestoredDraft(true);
    } catch (err) {
      console.warn("Could not load auto-saved draft:", err);
    }
  }, [draftParam]);

  // Auto-Save: Persist draft on field changes
  useEffect(() => {
    if (isDraftLocked || draftParam) return;
    if (currentStep === 1 && !fullName && !mobileNumber) return;

    const draftData = {
      currentStep,
      visitType,
      fullName,
      mobileNumber,
      isOtpVerified,
      otpToken,
      selfieDataUrl,
      deliveryCompany,
      orderId,
      vehicleNumber,
      destinationMode,
      departmentId,
      selectedDeptObj,
      hostUserId,
      selectedHostName,
      selectedHostObj,
      purpose,
      noOfVisitors,
      dpdpConsent,
      savedAt: Date.now(),
    };

    try {
      localStorage.setItem("asmita_visitor_checkin_draft_v2", JSON.stringify(draftData));
    } catch (err) {
      try {
        const draftWithoutPhoto = { ...draftData };
        delete draftWithoutPhoto.selfieDataUrl;
        localStorage.setItem("asmita_visitor_checkin_draft_v2", JSON.stringify(draftWithoutPhoto));
      } catch (e) {
        console.warn("Could not auto-save draft:", e);
      }
    }
  }, [
    currentStep,
    visitType,
    fullName,
    mobileNumber,
    isOtpVerified,
    otpToken,
    selfieDataUrl,
    deliveryCompany,
    orderId,
    vehicleNumber,
    destinationMode,
    departmentId,
    selectedDeptObj,
    hostUserId,
    selectedHostName,
    selectedHostObj,
    purpose,
    noOfVisitors,
    dpdpConsent,
    isDraftLocked,
    draftParam,
  ]);

  // Auto-set defaults when Entry Type changes
  useEffect(() => {
    if (visitType === "DELIVERY") {
      setNoOfVisitors(1);
      setPurpose("Delivery / Courier Drop");
    } else {
      if (purpose === "Delivery / Courier Drop") {
        setPurpose("");
      }
    }
  }, [visitType]);

  // Indian HSRP Vehicle Number Plate Helper Functions
  const updateCombinedVehicleNumber = (st, rto, ser, num) => {
    isSyncingPlateRef.current = true;
    const parts = [st, rto, ser, num].map((p) => (p || "").trim().toUpperCase()).filter(Boolean);
    const combined = parts.join(" ");
    setVehicleNumber(combined);
    setTimeout(() => {
      isSyncingPlateRef.current = false;
    }, 50);
  };

  const handlePlateStateChange = (val) => {
    const clean = val.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase();
    setPlateState(clean);
    updateCombinedVehicleNumber(clean, plateRto, plateSeries, plateNumber);
    if (clean.length === 2 && plateRtoRef.current) {
      plateRtoRef.current.focus();
    }
  };

  const handlePlateRtoChange = (val) => {
    const clean = val.replace(/[^0-9]/g, "").slice(0, 2);
    setPlateRto(clean);
    updateCombinedVehicleNumber(plateState, clean, plateSeries, plateNumber);
    if (clean.length === 2 && plateSeriesRef.current) {
      plateSeriesRef.current.focus();
    }
  };

  const handlePlateSeriesChange = (val) => {
    const clean = val.replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase();
    setPlateSeries(clean);
    updateCombinedVehicleNumber(plateState, plateRto, clean, plateNumber);
    if (clean.length >= 2 && plateNumberRef.current) {
      plateNumberRef.current.focus();
    }
  };

  const handlePlateNumberChange = (val) => {
    const clean = val.replace(/[^0-9]/g, "").slice(0, 4);
    setPlateNumber(clean);
    updateCombinedVehicleNumber(plateState, plateRto, plateSeries, clean);
  };

  const handlePlateKeyDown = (field, e) => {
    if (e.key === "Backspace") {
      if (field === "rto" && !plateRto && plateStateRef.current) {
        plateStateRef.current.focus();
      } else if (field === "series" && !plateSeries && plateRtoRef.current) {
        plateRtoRef.current.focus();
      } else if (field === "number" && !plateNumber && plateSeriesRef.current) {
        plateSeriesRef.current.focus();
      }
    }
  };

  const handlePlatePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text") || "").trim();
    const clean = pasted.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const match = clean.match(/^([A-Z]{2})([0-9]{1,2})([A-Z]{0,3})([0-9]{1,4})$/);
    if (match) {
      const s = match[1] || "";
      const r = match[2] || "";
      const ser = match[3] || "";
      const num = match[4] || "";
      setPlateState(s);
      setPlateRto(r);
      setPlateSeries(ser);
      setPlateNumber(num);
      updateCombinedVehicleNumber(s, r, ser, num);
      if (plateNumberRef.current) plateNumberRef.current.focus();
    } else {
      setVehicleNumber(pasted.toUpperCase());
      setUseSinglePlateInput(true);
    }
  };

  const handleClearPlate = () => {
    setPlateState("");
    setPlateRto("");
    setPlateSeries("");
    setPlateNumber("");
    setVehicleNumber("");
  };

  // Sync external vehicleNumber changes (e.g. Draft / Returning Visitor) into Plate Boxes
  useEffect(() => {
    if (isSyncingPlateRef.current) return;
    if (vehicleNumber) {
      const currentCombined = [plateState, plateRto, plateSeries, plateNumber]
        .filter(Boolean)
        .join(" ")
        .trim()
        .toUpperCase();
      if (currentCombined !== vehicleNumber.trim().toUpperCase()) {
        const clean = vehicleNumber.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
        const match = clean.match(/^([A-Z]{2})([0-9]{1,2})([A-Z]{0,3})([0-9]{1,4})$/);
        if (match) {
          setPlateState(match[1] || "");
          setPlateRto(match[2] || "");
          setPlateSeries(match[3] || "");
          setPlateNumber(match[4] || "");
        } else {
          setUseSinglePlateInput(true);
        }
      }
    } else {
      if (plateState || plateRto || plateSeries || plateNumber) {
        setPlateState("");
        setPlateRto("");
        setPlateSeries("");
        setPlateNumber("");
      }
    }
  }, [vehicleNumber]);

  const handleProceedToStep3 = () => {
    if (!fullName.trim() || fullName.trim().length < 2) {
      toast.warn("Please enter your full name as per ID proof");
      return;
    }
    if (!mobileNumber || mobileNumber.length !== 10) {
      toast.warn("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!isOtpVerified) {
      toast.warn("Please verify your mobile number with OTP code before proceeding");
      return;
    }
    if (visitType === "VEHICLE" && !vehicleNumber.trim()) {
      toast.warn("Please enter your vehicle registration number");
      return;
    }
    if (visitType === "DELIVERY" && !deliveryCompany.trim()) {
      toast.warn("Please specify your delivery company or app name");
      return;
    }
    if (visitType !== "DELIVERY" && !selfieDataUrl && !isReturningVisitor) {
      toast.warn("Please take a live security photo before proceeding");
      return;
    }
    setCurrentStep(3);
  };

  const handleClearDraft = () => {
    try {
      localStorage.removeItem("asmita_visitor_checkin_draft_v2");
    } catch (e) {
      console.warn("Could not remove draft", e);
    }
    setHasRestoredDraft(false);
    clearCheckinSession();
    setCurrentStep(1);
    toast.info("Cleared saved progress. Starting fresh check-in.");
  };

  // Resend OTP Countdown
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  // 2. Fetch Departments & Property Info
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await fetch(`/api/public/visitor/departments?society_id=${societyId}`);
        const data = await res.json();
        if (data.success) {
          setDepartments(data.departments || []);
          if (data.society_name || data.property_name) {
            setPropertyName(data.society_name || data.property_name);
          }
        }
      } catch (err) {
        console.error("Departments fetch error:", err);
      }
    };
    fetchDepartments();
  }, [societyId]);

  // 3. Automatically fetch employees in selected department
  useEffect(() => {
    if (!departmentId) {
      setDepartmentEmployees([]);
      return;
    }

    let isMounted = true;
    const fetchDeptEmployees = async () => {
      setLoadingDeptEmployees(true);
      try {
        const res = await fetch(`/api/public/visitor/search-hosts?society_id=${societyId}&department_id=${departmentId}`);
        const data = await res.json();
        if (isMounted && data.success) {
          setDepartmentEmployees(data.hosts || data.data || []);
        }
      } catch (err) {
        console.error("Error fetching department employees:", err);
      } finally {
        if (isMounted) setLoadingDeptEmployees(false);
      }
    };

    fetchDeptEmployees();
    return () => {
      isMounted = false;
    };
  }, [departmentId, societyId]);

  // 4. Search Hosts (Employees) - Scoped to selected department if one is chosen
  useEffect(() => {
    if (!hostSearchQuery || hostSearchQuery.trim().length < 2) {
      setHostResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchingHosts(true);
      try {
        const queryParams = new URLSearchParams({
          query: hostSearchQuery.trim(),
          society_id: societyId,
          mode: "name",
        });
        if (destinationMode === "DEPARTMENT" && departmentId) {
          queryParams.append("department_id", departmentId);
        }
        const res = await fetch(`/api/public/visitor/search-hosts?${queryParams.toString()}`);
        const data = await res.json();
        if (data.success) {
          const q = hostSearchQuery.trim().toLowerCase();
          const isNum = /^\d+$/.test(q);
          const raw = data.hosts || data.data || [];
          // Multi-attribute matching: matches name, designation, role, department name, or phone
          const nameMatched = raw.filter((h) => {
            const fullName = (h.full_name || h.name || "").toLowerCase();
            const role = (h.primary_role || h.role || "").toLowerCase();
            const desig = (h.designation || "").toLowerCase();
            const dept = (h.department_name || h.department || "").toLowerCase();
            const phone = (h.mobile_number || h.phone || "");
            if (isNum && phone.includes(q)) return true;
            if (fullName.includes(q)) return true;
            if (role.includes(q)) return true;
            if (desig.includes(q)) return true;
            if (dept.includes(q)) return true;
            return false;
          });
          setHostResults(nameMatched);
        }
      } catch (err) {
        console.error("Host search error:", err);
      } finally {
        setSearchingHosts(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [hostSearchQuery, departmentId, destinationMode, societyId]);

  // If visitor typed a known department (e.g. "IT", "HR", "Accounts") in Person to Meet, offer 1-tap switch
  const matchingDeptSuggestion = useMemo(() => {
    if (!hostSearchQuery || hostSearchQuery.trim().length < 2) return null;
    const q = hostSearchQuery.trim().toLowerCase();
    return departments.find((d) => {
      const name = (d.department_name || d.name || "").toLowerCase();
      return name === q || name.includes(q);
    });
  }, [hostSearchQuery, departments]);

  // Filtered departments - strictly matches department name
  const filteredDepartments = useMemo(() => {
    if (!deptSearchQuery || deptSearchQuery.trim().length < 1) {
      return departments.slice(0, 8);
    }
    const q = deptSearchQuery.toLowerCase().trim();
    return departments.filter((d) => {
      const name = (d.department_name || d.name || "").toLowerCase();
      return name.includes(q);
    });
  }, [departments, deptSearchQuery]);

  // 4. Send OTP
  const handleSendOtp = async () => {
    if (!mobileNumber || mobileNumber.length !== 10) {
      toast.warn("Please enter a valid 10-digit mobile number");
      return;
    }

    setOtpLoading(true);
    try {
      const res = await fetch("/api/public/visitor/otp/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: mobileNumber,
          society_id: Number(societyId),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setOtpSent(true);
        setOtpToken(data.token || data.verificationToken || data.otp_token || data.phone || "");
        setResendCountdown(60);
        setOtpDigits(["", "", "", "", "", ""]);
        toast.success(data.message || "OTP sent successfully to your mobile number");
        setTimeout(() => {
          if (otpInputRefs.current[0]) otpInputRefs.current[0].focus();
        }, 150);
      } else {
        toast.error(data.error || data.message || "Failed to send OTP. Please ask gate guard.");
      }
    } catch (err) {
      toast.error("Network error sending OTP. Please consult the security guard.");
    } finally {
      setOtpLoading(false);
    }
  };

  // OTP Box Digit Handlers
  const handleOtpDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextDigits = [...otpDigits];
    nextDigits[index] = digit;
    setOtpDigits(nextDigits);

    // Auto-advance cursor
    if (digit && index < 5 && otpInputRefs.current[index + 1]) {
      otpInputRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      if (otpInputRefs.current[index - 1]) {
        otpInputRefs.current[index - 1].focus();
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const nextDigits = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) {
      nextDigits[i] = pasted[i];
    }
    setOtpDigits(nextDigits);
    const nextFocus = Math.min(pasted.length, 5);
    if (otpInputRefs.current[nextFocus]) {
      otpInputRefs.current[nextFocus].focus();
    }
  };

  // 5. Verify OTP + Auto-Fill Returning Visitor
  const handleVerifyOtp = async () => {
    const enteredOtp = otpDigits.join("");
    if (enteredOtp.length < 4) {
      toast.warn("Please enter all 6 digits of the OTP code");
      return;
    }

    setOtpLoading(true);
    try {
      const res = await fetch("/api/public/visitor/otp/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: mobileNumber,
          otp: enteredOtp,
          token: otpToken,
          verification_token: otpToken,
          property_id: Number(societyId),
          society_id: Number(societyId),
        }),
      });
      const data = await res.json();
      if (data.success) {
        setIsOtpVerified(true);
        setOtpToken(data.verificationToken || data.otp_token || data.token || otpToken);

        // Check if returning visitor
        if (data.is_returning_visitor && data.previous_visitor) {
          const prev = data.previous_visitor;
          setIsReturningVisitor(true);
          setReturningData(prev);

          if (prev.full_name) setFullName(prev.full_name);
          if (prev.vehicle_number) {
            setVehicleNumber(prev.vehicle_number);
            setVisitType("VEHICLE");
          } else {
            setVisitType(prev.arrival_mode === "VEHICLE" ? "VEHICLE" : "VISITOR");
          }
          if (prev.delivery_company_name) setDeliveryCompany(prev.delivery_company_name);
          if (prev.photo_url) {
            setSelfieDataUrl(prev.photo_url);
          }
          if (prev.last_department_id) setDepartmentId(String(prev.last_department_id));
          if (prev.last_host_id) {
            setHostUserId(String(prev.last_host_id));
            setSelectedHostName(prev.last_host_name || "Assigned Host");
          }
          // Clear purpose so adding reason to meet remains mandatory today
          setPurpose("");
          setDpdpConsent(true);

          toast.success(`Identity verified! Welcome back, ${prev.full_name}.`);
        } else {
          toast.success("Mobile number verified successfully!");
        }
      } else {
        toast.error(data.error || data.message || "Invalid OTP code");
      }
    } catch (err) {
      toast.error("Error verifying OTP. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  // 6. Submit Checkin Request
  const handleSubmit = async (e, customHostId = null, customDeptId = null) => {
    if (e && e.preventDefault) e.preventDefault();

    const targetFullName = fullName.trim();
    if (!targetFullName || targetFullName.length < 2) {
      toast.warn("Please enter your full name as per official photo ID");
      return;
    }
    if (!mobileNumber.trim() || mobileNumber.trim().length !== 10) {
      toast.warn("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!isOtpVerified) {
      toast.warn("Please verify your mobile number with the OTP code first");
      return;
    }

    const targetDeptId = customDeptId || departmentId || selectedHostObj?.department_id;
    if (!customHostId && !hostUserId && !targetDeptId) {
      toast.warn("Please search and select the host employee or department you are visiting");
      return;
    }
    if (visitType === "VEHICLE" && (!vehicleNumber || vehicleNumber.trim().length < 3)) {
      toast.warn("Please enter your vehicle registration number");
      return;
    }
    if (visitType === "DELIVERY" && !deliveryCompany.trim()) {
      toast.warn("Please enter or select your delivery company or service");
      return;
    }
    if (!isReturningVisitor && visitType !== "DELIVERY" && !selfieDataUrl) {
      toast.warn("A live security photo is mandatory. Please capture a selfie before submitting.");
      return;
    }
    if (visitType !== "DELIVERY" && !purpose.trim()) {
      toast.warn("Please select or enter the purpose of your visit");
      return;
    }
    if (!dpdpConsent) {
      toast.warn("Please accept the data protection consent to proceed");
      return;
    }

    setSubmitting(true);
    try {
      const isBase64Photo = selfieDataUrl && selfieDataUrl.startsWith("data:");
      const isStoredPhotoUrl = selfieDataUrl && !selfieDataUrl.startsWith("data:");

      const resolvedDeptId = customDeptId
        ? Number(customDeptId)
        : departmentId
        ? Number(departmentId)
        : selectedHostObj?.department_id
        ? Number(selectedHostObj.department_id)
        : departments[0]?.id || departments[0]?.department_id
        ? Number(departments[0]?.id || departments[0]?.department_id)
        : 1;

      const fullPurpose =
        purpose.trim() ||
        (visitType === "DELIVERY" ? "Delivery / Courier Drop" : "Visitor Entry");

      const payload = {
        society_id: Number(societyId),
        gate_id: Number(gateParam),
        visit_type: visitType,
        visitor_name: targetFullName,
        mobile_number: mobileNumber.trim(),
        vehicle_number: vehicleNumber ? vehicleNumber.trim().toUpperCase() : null,
        department_id: resolvedDeptId,
        host_user_id: customHostId || (hostUserId ? Number(hostUserId) : null),
        purpose: fullPurpose,
        number_of_visitors: Number(noOfVisitors) || 1,
        delivery_company_name: deliveryCompany ? deliveryCompany.trim() : null,
        delivery_order_id: orderId ? orderId.trim() : null,
        photo_url: isStoredPhotoUrl ? selfieDataUrl : null,
        selfie_base64: isBase64Photo ? selfieDataUrl : null,
        verification_token: otpToken,
        draft_id: draftParam || null,
        consent_obtained: true,
      };

      const res = await fetch("/api/public/visitor/checkin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success && (data.request_number || data.status_token)) {
        toast.success("Visitor entry request submitted successfully!");

        const targetCode = data.status_token || data.request_number;
        try {
          localStorage.setItem(
            "asmita_active_pass",
            JSON.stringify({
              pass_code: targetCode,
              request_number: data.request_number,
              status_token: targetCode,
              phone: mobileNumber.trim(),
              visitor_name: fullName.trim(),
              society_id: Number(societyId),
              timestamp: Date.now(),
            })
          );
        } catch (err) {
          // Ignore localStorage errors
        }

        clearCheckinSession();
        navigate(`/visitor/status/${targetCode}`, { replace: true });
      } else {
        toast.error(data.error || data.message || "Submission failed. Please contact the security guard.");
      }
    } catch (err) {
      toast.error("Network error during submission. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const clearCheckinSession = () => {
    setFullName("");
    setMobileNumber("");
    setVehicleNumber("");
    setDepartmentId("");
    setHostUserId("");
    setHostSearchQuery("");
    setSelectedHostName("");
    setSelectedHostObj(null);
    setDeptSearchQuery("");
    setSelectedDeptObj(null);
    setDepartmentEmployees([]);
    setPurpose("");
    setNoOfVisitors(1);
    setDeliveryCompany("");
    setOrderId("");
    setDpdpConsent(false);
    setOtpSent(false);
    setOtpDigits(["", "", "", "", "", ""]);
    setOtpToken("");
    setIsOtpVerified(false);
    setIsDraftLocked(false);
    setDraftCountdown(null);
    setSelfieDataUrl(null);
    try {
      sessionStorage.removeItem("visitor_checkin_draft");
      sessionStorage.removeItem("visitor_checkin_phone");
      localStorage.removeItem("asmita_visitor_checkin_draft_v2");
    } catch (e) {
      console.warn("Could not clear session storage", e);
    }
    setHasRestoredDraft(false);
    setCurrentStep(1);
  };

  // Reusable destination selector component for employee vs department search
  const renderDestinationSelector = () => (
    destinationMode === "EMPLOYEE" ? (
      <div className={styles.destinationField}>
        <div className={styles.destinationHeader}>
          <label className={styles.fieldLabel}>
            Whom do you want to meet? (Staff Name) <span className={styles.requiredStar}>*</span>
          </label>
          <button
            type="button"
            className={styles.modeToggleBtn}
            onClick={() => {
              setDestinationMode("DEPARTMENT");
              setDeptSearchQuery("");
            }}
          >
            <Building size={13} />
            Visiting an entire department instead?
          </button>
        </div>

        {selectedHostObj || selectedHostName ? (
          <div className={styles.hostSelectedCard}>
            <div className={styles.hostSelectedInfo}>
              <div className={styles.hostAvatar}>
                <User size={18} />
              </div>
              <div className={styles.hostDetails}>
                <span className={styles.hostName}>
                  {selectedHostObj?.name || selectedHostObj?.full_name || selectedHostName}
                </span>
                <div className={styles.hostMeta}>
                  {(selectedHostObj?.designation || selectedHostObj?.role || selectedHostObj?.primary_role) && (
                    <span className={styles.hostBadge}>
                      {selectedHostObj.designation || selectedHostObj.role || selectedHostObj.primary_role}
                    </span>
                  )}
                  {selectedHostObj?.department_name && (
                    <span className={styles.hostDeptText}>
                      • {selectedHostObj.department_name}
                      {selectedHostObj.floor ? ` (Floor ${String(selectedHostObj.floor).replace(/^floor\s*/i, "")})` : ""}
                    </span>
                  )}
                </div>
                {selectedHostObj?.email && (
                  <span className={styles.hostEmailText}>
                    {selectedHostObj.email}
                  </span>
                )}
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setHostUserId("");
                setSelectedHostName("");
                setSelectedHostObj(null);
              }}
              icon={<X size={14} />}
            >
              Change
            </Button>
          </div>
        ) : (
          <div className={styles.searchBlock}>
            <SearchInput
              fullWidth
              className={styles.fullWidthSearch}
              placeholder="Type staff name to search..."
              value={hostSearchQuery}
              onChange={(val) => setHostSearchQuery(val)}
            />

            {/* Smart Department Hint if query matches a known department like "IT" */}
            {matchingDeptSuggestion && (
              <div className={styles.deptSuggestionBanner}>
                <span>Looking for the <strong>{matchingDeptSuggestion.department_name || matchingDeptSuggestion.name}</strong> department?</span>
                <button
                  type="button"
                  className={styles.switchDeptLink}
                  onClick={() => {
                    setDepartmentId(String(matchingDeptSuggestion.department_id || matchingDeptSuggestion.id));
                    setSelectedDeptObj(matchingDeptSuggestion);
                    setDestinationMode("DEPARTMENT");
                    setHostSearchQuery("");
                  }}
                >
                  Select Department &rarr;
                </button>
              </div>
            )}

            {hostSearchQuery.trim().length === 0 && (
              <div className={styles.searchHint}>
                Type at least 2 letters of their name...
              </div>
            )}

            {searchingHosts && (
              <div className={styles.searchHint}>
                <Clock size={12} className={styles.spinIcon} /> Searching staff directory...
              </div>
            )}

            {hostSearchQuery.trim().length >= 2 && !searchingHosts && hostResults.length === 0 && (
              <div className={styles.noResultsMessage}>
                No staff member found matching &quot;{hostSearchQuery}&quot;. Please check the spelling or switch to department search.
              </div>
            )}

            {hostResults.length > 0 && (
              <div className={styles.searchResultsList}>
                {hostResults.map((h) => {
                  const cleanFloor = h.floor ? String(h.floor).replace(/^floor\s*/i, "").trim() : "";
                  return (
                    <div
                      key={h.user_id}
                      className={styles.searchResultItem}
                      onClick={() => {
                        setHostUserId(h.user_id);
                        setSelectedHostName(
                          `${h.full_name || h.name}${
                            h.primary_role ? ` (${h.primary_role})` : ""
                          }`
                        );
                        setSelectedHostObj(h);
                        if (h.department_id) {
                          setDepartmentId(String(h.department_id));
                        }
                        setHostResults([]);
                        setHostSearchQuery("");
                      }}
                    >
                      <div className={styles.searchResultMain}>
                        <div className={styles.searchResultTitleRow}>
                          <span className={styles.searchResultName}>{h.name || h.full_name}</span>
                          {(h.designation || h.role || h.primary_role) && (
                            <span className={styles.searchResultBadge}>
                              {h.designation || h.role || h.primary_role}
                            </span>
                          )}
                        </div>
                        {h.department_name && (
                          <span className={styles.searchResultSubtitle}>
                            <Building size={12} />
                            {h.department_name}
                            {cleanFloor ? ` • Floor ${cleanFloor}` : ""}
                          </span>
                        )}
                        {h.email && (
                          <span className={styles.searchResultEmail}>
                            {h.email}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    ) : (
      <div className={styles.destinationField}>
        <div className={styles.destinationHeader}>
          <label className={styles.fieldLabel}>
            Which Department / Office are you visiting? <span className={styles.requiredStar}>*</span>
          </label>
          <button
            type="button"
            className={styles.modeToggleBtn}
            onClick={() => {
              setDestinationMode("EMPLOYEE");
              setHostSearchQuery("");
              setDepartmentId("");
              setSelectedDeptObj(null);
            }}
          >
            <User size={13} />
            Looking for a specific person instead?
          </button>
        </div>

        {selectedDeptObj || departmentId ? (
          <div className={styles.deptSelectedWrap}>
            <div className={styles.hostSelectedCard}>
              <div className={styles.hostSelectedInfo}>
                <div className={styles.hostAvatar}>
                  <Building size={18} />
                </div>
                <div className={styles.hostDetails}>
                  <span className={styles.hostName}>
                    {selectedDeptObj?.name ||
                      departments.find((d) => String(d.department_id || d.id) === String(departmentId))?.name ||
                      "Selected Department"}
                  </span>
                  <div className={styles.hostMeta}>
                    <span className={styles.hostBadge}>Department</span>
                    {(selectedDeptObj?.floor || departments.find((d) => String(d.department_id || d.id) === String(departmentId))?.floor) && (
                      <span className={styles.hostDeptText}>
                        Floor {String(selectedDeptObj?.floor || departments.find((d) => String(d.department_id || d.id) === String(departmentId))?.floor).replace(/^floor\s*/i, "")}
                      </span>
                    )}
                    {selectedHostObj && (
                      <span className={styles.hostDeptText}>
                        • Meeting with <strong>{selectedHostObj.full_name || selectedHostObj.name}</strong>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDepartmentId("");
                  setSelectedDeptObj(null);
                  setDepartmentEmployees([]);
                  setHostUserId("");
                  setSelectedHostName("");
                  setSelectedHostObj(null);
                }}
                icon={<X size={14} />}
              >
                Change
              </Button>
            </div>

            {/* Department Employees List */}
            <div className={styles.deptStaffSection}>
              <div className={styles.deptStaffHeader}>
                <span className={styles.deptStaffLabel}>
                  Select specific person to meet (or meet department front desk):
                </span>
              </div>

              {loadingDeptEmployees ? (
                <div className={styles.searchHint}>
                  <Clock size={12} className={styles.spinIcon} />
                  <span>Loading employees in this department...</span>
                </div>
              ) : departmentEmployees.length > 0 ? (
                <div className={styles.deptStaffList}>
                  {/* Option 1: General Department Desk */}
                  <div
                    className={`${styles.deptStaffItem} ${!hostUserId ? styles.deptStaffItemActive : ""}`}
                    onClick={() => {
                      setHostUserId("");
                      setSelectedHostName("");
                      setSelectedHostObj(null);
                    }}
                  >
                    <div className={styles.deptStaffAvatar}>
                      <Building size={16} />
                    </div>
                    <div className={styles.deptStaffInfo}>
                      <span className={styles.deptStaffName}>Department Front Desk / General</span>
                      <span className={styles.deptStaffSub}>General visit to this department</span>
                    </div>
                    {!hostUserId && (
                      <div className={styles.deptStaffCheck}>
                        <Check size={14} />
                      </div>
                    )}
                  </div>

                  {/* Option 2: Employees belonging to this department */}
                  {departmentEmployees.map((emp) => {
                    const isSelected = String(hostUserId) === String(emp.user_id || emp.id);
                    return (
                      <div
                        key={emp.user_id || emp.id}
                        className={`${styles.deptStaffItem} ${isSelected ? styles.deptStaffItemActive : ""}`}
                        onClick={() => {
                          setHostUserId(String(emp.user_id || emp.id));
                          setSelectedHostName(emp.full_name || emp.name);
                          setSelectedHostObj(emp);
                        }}
                      >
                        <div className={styles.deptStaffAvatar}>
                          <User size={16} />
                        </div>
                        <div className={styles.deptStaffInfo}>
                          <div className={styles.deptStaffNameRow}>
                            <span className={styles.deptStaffName}>{emp.full_name || emp.name}</span>
                            {(emp.designation || emp.primary_role || emp.role) && (
                              <span className={styles.hostBadge}>
                                {emp.designation || emp.primary_role || emp.role}
                              </span>
                            )}
                          </div>
                          {(emp.floor || emp.floor_number) && (
                            <span className={styles.deptStaffSub}>
                              Floor {String(emp.floor || emp.floor_number).replace(/^floor\s*/i, "")}
                              {emp.cabin_code ? ` • Cabin ${emp.cabin_code}` : ""}
                              {emp.desk_code ? ` • Desk ${emp.desk_code}` : ""}
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <div className={styles.deptStaffCheck}>
                            <Check size={14} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.deptEmptyStaffNote}>
                  <Info size={13} />
                  <span>No individual employees listed for this department. You will be received at the department desk.</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.searchBlock}>
            {/* Quick Department Chips */}
            <div className={styles.quickDeptChipsWrap}>
              <span className={styles.quickChipsLabel}>Common Departments:</span>
              <div className={styles.chipsRow}>
                {departments.slice(0, 5).map((d) => (
                  <button
                    key={d.department_id || d.id}
                    type="button"
                    className={`${styles.pillChip} ${String(departmentId) === String(d.department_id || d.id) ? styles.activePillChip : ""}`}
                    onClick={() => {
                      setDepartmentId(String(d.department_id || d.id));
                      setSelectedDeptObj(d);
                    }}
                  >
                    {d.department_name || d.name}
                  </button>
                ))}
              </div>
            </div>

            <SearchInput
              fullWidth
              className={styles.fullWidthSearch}
              placeholder="Search by department name (e.g. IT, Accounts, HR)..."
              value={deptSearchQuery}
              onChange={(val) => setDeptSearchQuery(val)}
            />

            {filteredDepartments.length > 0 && (
              <div className={styles.searchResultsList}>
                {filteredDepartments.map((d) => {
                  const rawFloor = d.floor_number || d.floor || "";
                  const cleanFloor = rawFloor ? String(rawFloor).replace(/^floor\s*/i, "").trim() : "";
                  return (
                    <div
                      key={d.department_id || d.id}
                      className={styles.searchResultItem}
                      onClick={() => {
                        setDepartmentId(String(d.department_id || d.id));
                        setSelectedDeptObj(d);
                        setDeptSearchQuery("");
                      }}
                    >
                      <div className={styles.searchResultMain}>
                        <div className={styles.searchResultTitleRow}>
                          <span className={styles.searchResultName}>
                            {d.department_name || d.name}
                          </span>
                          {cleanFloor && (
                            <span className={styles.searchResultBadge}>
                              Floor {cleanFloor}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    )
  );

  return (
    <div className={styles.pageContainer}>
      {/* ========================================================= */}
      {/* 1. DEVICE LOCKED OVERLAY (When device has active pass)    */}
      {/* ========================================================= */}
      {showActivePrompt && activeVisit && !showMeetAnotherModal && (
        <div
          className={styles.activeModalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="device-locked-title"
        >
          <div className={styles.activeModalCard}>
            {/* Security Lock Header */}
            <div className={styles.deviceLockHeader}>
              <div className={styles.lockIconShieldWrap}>
                <div className={styles.lockIconShieldHalo} />
                <div className={styles.lockIconShield}>
                  <Lock size={26} className={styles.lockIconSvg} />
                </div>
              </div>

              <div className={styles.deviceLockPill}>
                <ShieldAlert size={13} />
                <span>Device Locked • Active Pass</span>
              </div>

              <h2 id="device-locked-title" className={styles.activeModalTitle}>
                Device Locked to Active Pass
              </h2>
              <p className={styles.activeModalSubtitle}>
                This device holds an active visitor pass for {propertyName || "AsmitA Operations"}. Duplicate check-ins are restricted to maintain premises security.
              </p>

              {activeVisit._isLiveUpdating && (
                <div className={styles.liveSyncingBadge}>
                  <RotateCcw size={11} className={styles.spinIcon} />
                  <span>Syncing live status...</span>
                </div>
              )}
            </div>

            {/* Active Pass Card Details */}
            <div className={styles.passCardPreview}>
              <div className={styles.passPreviewTopRow}>
                <div className={styles.passPreviewCode}>
                  <span className={styles.passPreviewCodeLabel}>Active Pass Code</span>
                  <div className={styles.codeWithCopyRow}>
                    <span className={styles.passPreviewCodeValue}>
                      {activeVisit.pass_code || activeVisit.request_number || activeVisit.status_token || "PASS-ACTIVE"}
                    </span>
                    <button
                      type="button"
                      className={styles.copyPassBtn}
                      onClick={() => {
                        const targetCode = activeVisit.pass_code || activeVisit.request_number || activeVisit.status_token;
                        if (targetCode && navigator?.clipboard?.writeText) {
                          navigator.clipboard.writeText(targetCode);
                          setCopiedPassCode(true);
                          toast.success("Pass code copied!");
                          setTimeout(() => setCopiedPassCode(false), 2000);
                        }
                      }}
                      title="Copy Pass Code"
                    >
                      {copiedPassCode ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                      <span>{copiedPassCode ? "Copied" : "Copy"}</span>
                    </button>
                  </div>
                </div>
                <StatusBadge
                  status={
                    activeVisit.status ||
                    (activeVisit.is_checked_in ? "CHECKED_IN" : activeVisit.is_approved ? "APPROVED" : "AWAITING_HOST")
                  }
                  showDot
                />
              </div>

              <div className={styles.passPreviewDivider} />

              <div className={styles.passPreviewGrid}>
                <div className={styles.passPreviewItem}>
                  <span className={styles.passPreviewItemLabel}>Visitor Name</span>
                  <span className={styles.passPreviewItemValue}>
                    {activeVisit.visitor_name || "Visitor"}
                  </span>
                  {(activeVisit.visitor_phone || activeVisit.phone) && (
                    <span className={styles.passPreviewItemSub}>
                      +91 {activeVisit.visitor_phone || activeVisit.phone}
                    </span>
                  )}
                </div>

                <div className={styles.passPreviewItem}>
                  <span className={styles.passPreviewItemLabel}>Host & Department</span>
                  <span className={styles.passPreviewItemValue}>
                    {activeVisit.host_name || "Department Team"}
                  </span>
                  <span className={styles.passPreviewItemSub}>
                    {activeVisit.department_name || "General Facility"}
                  </span>
                </div>

                <div className={styles.passPreviewItem}>
                  <span className={styles.passPreviewItemLabel}>Floor & Cabin</span>
                  <span className={styles.passPreviewItemValue}>
                    Floor {activeVisit.floor_number ?? "Ground"} • Cabin {activeVisit.cabin_code || "Main Desk"}
                  </span>
                  {activeVisit.desk_code && activeVisit.desk_code !== "TBD" && (
                    <span className={styles.passPreviewItemSub}>Desk: {activeVisit.desk_code}</span>
                  )}
                </div>

                <div className={styles.passPreviewItem}>
                  <span className={styles.passPreviewItemLabel}>Gate & Mode</span>
                  <span className={styles.passPreviewItemValue}>
                    Gate {activeVisit.gate_id || gateParam || "1"}
                  </span>
                  <span className={styles.passPreviewItemSub}>
                    {activeVisit.vehicle_number ? `Vehicle: ${activeVisit.vehicle_number}` : "Pedestrian Arrival"}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.activeActions}>
              <button
                type="button"
                className={styles.activePrimaryBtn}
                onClick={() => {
                  const targetCode = activeVisit.status_token || activeVisit.pass_code || activeVisit.request_number;
                  navigate(`/visitor/status/${targetCode}`);
                }}
              >
                <div className={styles.activeBtnTextWrap}>
                  <span className={styles.activeBtnTitle}>View Active Pass & Instructions</span>
                  <span className={styles.activeBtnSubtitle}>Open live QR pass badge, check host status & cabin</span>
                </div>
                <ArrowRight size={20} className={styles.activeBtnArrow} />
              </button>

              <button
                type="button"
                className={styles.activeSecondaryBtn}
                onClick={() => {
                  setShowMeetAnotherModal(true);
                }}
              >
                <div className={styles.activeBtnIconWrap}>
                  <UserPlus size={18} />
                </div>
                <div className={styles.activeBtnTextWrap}>
                  <span className={styles.activeBtnTitle}>Meet Another Person (PTM)</span>
                  <span className={styles.activeBtnSubtitle}>Transfer or add another host from staff directory</span>
                </div>
              </button>

              {/* Unlock Device / Shared Terminal Action */}
              {!showUnlockConfirm ? (
                <button
                  type="button"
                  className={styles.unlockPromptBtn}
                  onClick={() => setShowUnlockConfirm(true)}
                >
                  <KeyRound size={14} />
                  <span>Using a shared device or different visitor? Unlock Device</span>
                </button>
              ) : (
                <div className={styles.unlockConfirmBox}>
                  <div className={styles.unlockConfirmText}>
                    <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: "2px" }} />
                    <span>Release this device? This will clear the active pass session from this browser.</span>
                  </div>
                  <div className={styles.unlockConfirmActions}>
                    <button
                      type="button"
                      className={styles.unlockCancelBtn}
                      onClick={() => setShowUnlockConfirm(false)}
                    >
                      Keep Active Pass
                    </button>
                    <button
                      type="button"
                      className={styles.unlockConfirmBtn}
                      onClick={() => {
                        deviceUnlockedRef.current = true;
                        setShowActivePrompt(false);
                        setActiveVisit(null);
                        setShowUnlockConfirm(false);
                        try {
                          localStorage.removeItem("asmita_active_pass");
                        } catch (err) {
                          // Ignore
                        }
                        toast.success("Device unlocked successfully! You can now check in.");
                      }}
                    >
                      Yes, Unlock Device
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Assistance Footer */}
            <div className={styles.deviceLockFooter}>
              <span>Need help? Contact Gate {gateParam || "1"} Security Reception Desk</span>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* 2. APPLY TO MEET ANOTHER PERSON MODAL                     */}
      {/* ========================================================= */}
      {showMeetAnotherModal && activeVisit && (
        <div className={styles.activeModalOverlay}>
          <div className={`${styles.activeModalCard} ${styles.meetAnotherCard}`}>
            <div className={styles.meetAnotherHeader}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className={styles.brandBadge} style={{ marginBottom: 0 }}>
                  <UserPlus size={14} />
                  <span>Transfer Visit Host</span>
                </div>
                <button
                  type="button"
                  className={styles.modalCloseBtn}
                  onClick={() => setShowMeetAnotherModal(false)}
                >
                  <X size={18} />
                </button>
              </div>
              <h2 className={styles.activeModalTitle} style={{ marginTop: "12px" }}>Meet Another Person</h2>
              <p className={styles.activeModalSubtitle}>
                Select an employee from the staff directory to meet next during this visit.
              </p>
            </div>

            {/* Current Pass summary */}
            <div className={styles.currentHostSnippet}>
              <span>Current Visit:</span>
              <strong>{activeVisit.host_name || "Assigned Host"}</strong>
              <span>({activeVisit.department_name || "General"})</span>
            </div>

            {/* Host Search Input */}
            <div className={styles.meetAnotherSearchWrap}>
              <SearchInput
                fullWidth
                className={styles.fullWidthSearch}
                placeholder="Type staff name to search..."
                value={meetAnotherQuery}
                onChange={(v) => setMeetAnotherQuery(v)}
                autoFocus
              />
            </div>

            {/* Candidate Hosts List */}
            <div className={styles.candidateHostList}>
              {searchingMeetAnother ? (
                <div className={styles.searchingHostsBox}>
                  <Clock size={16} className={styles.spinIcon} /> Searching available staff...
                </div>
              ) : meetAnotherResults.length === 0 ? (
                <div className={styles.noHostsFoundBox}>
                  No matching staff member found. Try searching by full name.
                </div>
              ) : (
                meetAnotherResults.map((host) => {
                  const isSelected = selectedNewHost?.user_id === host.user_id;
                  return (
                    <div
                      key={host.user_id}
                      className={`${styles.candidateHostItem} ${isSelected ? styles.candidateHostSelected : ""}`}
                      onClick={() => setSelectedNewHost(host)}
                    >
                      <div className={styles.candidateHostAvatar}>
                        {host.full_name?.charAt(0)?.toUpperCase() || "H"}
                      </div>
                      <div className={styles.candidateHostInfo}>
                        <div className={styles.candidateHostNameRow}>
                          <span className={styles.candidateHostName}>{host.full_name}</span>
                          {host.department_name && (
                            <span className={styles.candidateHostDeptBadge}>{host.department_name}</span>
                          )}
                        </div>
                        <div className={styles.candidateHostSub}>
                          Floor {host.floor_number || 1} • Cabin {host.cabin_code || "Main Desk"} (Desk {host.desk_code || "Desk 1"})
                        </div>
                      </div>
                      <div className={styles.candidateHostCheck}>
                        {isSelected ? <CheckCircle2 size={18} color="#27306f" /> : <div className={styles.emptyRadio} />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Actions */}
            <div className={styles.meetAnotherFooter}>
              <Button
                variant="secondary"
                onClick={() => setShowMeetAnotherModal(false)}
                disabled={submittingMeetAnother}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmMeetAnother}
                disabled={!selectedNewHost || submittingMeetAnother}
                loading={submittingMeetAnother}
                trailIcon={<ArrowRight size={16} />}
              >
                Confirm & Apply
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* MAIN CHECKIN CARD                                         */}
      {/* ========================================================= */}
      <div className={styles.card}>
        {/* Header Branding */}
        <div className={styles.header}>
          <div className={styles.brandBadge}>
            <ShieldCheck size={14} />
            <span>Digital Visitor Entry • Gate {gateParam}</span>
          </div>
          <h1 className={styles.title}>{propertyName || "AsmitA Visitor Gate Pass"}</h1>
          <p className={styles.subtitle}>
            Quick Visitor Pass & Gate Entry
          </p>
        </div>

        {/* Top Progress Micro-Stepper (Only 3 steps: Entry Type -> Basic Details -> Visit Details) */}
        <div className={styles.stepperContainer}>
          <div
            className={`${styles.stepItem} ${
              currentStep === 1
                ? styles.stepActive
                : currentStep > 1
                ? styles.stepCompleted
                : styles.stepInactive
            }`}
            onClick={() => currentStep > 1 && setCurrentStep(1)}
            style={{ cursor: currentStep > 1 ? "pointer" : "default" }}
          >
            <div className={styles.stepCircle}>
              {currentStep > 1 ? <Check size={14} /> : "1"}
            </div>
            <span className={styles.stepLabel}>Entry Type</span>
          </div>

          <div
            className={`${styles.stepConnector} ${
              currentStep > 1 ? styles.connectorActive : ""
            }`}
          />

          <div
            className={`${styles.stepItem} ${
              currentStep === 2
                ? styles.stepActive
                : currentStep > 2
                ? styles.stepCompleted
                : styles.stepInactive
            }`}
            onClick={() => currentStep > 2 && setCurrentStep(2)}
            style={{ cursor: currentStep > 2 ? "pointer" : "default" }}
          >
            <div className={styles.stepCircle}>
              {currentStep > 2 ? <Check size={14} /> : "2"}
            </div>
            <span className={styles.stepLabel}>Basic Details</span>
          </div>

          <div
            className={`${styles.stepConnector} ${
              currentStep > 2 ? styles.connectorActive : ""
            }`}
          />

          <div
            className={`${styles.stepItem} ${
              currentStep === 3 ? styles.stepActive : styles.stepInactive
            }`}
          >
            <div className={styles.stepCircle}>3</div>
            <span className={styles.stepLabel}>Visit Details</span>
          </div>
        </div>

        {/* Draft Notification if Flow B */}
        {isDraftLocked && (
          <div className={styles.draftBanner}>
            <span>
              <strong>Gate Guard Entry:</strong> Pre-filled from parking gate
            </span>
            {draftCountdown !== null && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                <Clock size={14} /> {draftCountdown}s remaining
              </span>
            )}
          </div>
        )}

        {/* Auto-Save Restored Banner */}
        {hasRestoredDraft && (
          <div className={styles.restoredDraftBanner}>
            <div className={styles.restoredDraftInfo}>
              <RotateCcw size={14} />
              <span>Resumed from where you left off</span>
            </div>
            <button
              type="button"
              className={styles.clearDraftBtn}
              onClick={handleClearDraft}
            >
              Start Fresh
            </button>
          </div>
        )}

        {/* ========================================================= */}
        {/* STEP 1: SELECT ENTRY TYPE                                 */}
        {/* ========================================================= */}
        {currentStep === 1 && (
          <div className={styles.stepCard}>
            <div className={styles.stepHeader}>
              <div className={styles.stepHeaderIcon}>
                <Building size={20} />
              </div>
              <div>
                <h2 className={styles.stepHeaderTitle}>Select Your Entry Type</h2>
                <p className={styles.stepHeaderSub}>
                  How are you visiting today? Choose the option that best matches your visit.
                </p>
              </div>
            </div>

            <div className={styles.entryTypeGrid}>
              {ENTRY_TYPES.map((type) => {
                const isSelected = visitType === type.value;
                const badgeVariantClass =
                  type.badgeType === "warning"
                    ? styles.badgeWarning
                    : type.badgeType === "success"
                    ? styles.badgeSuccess
                    : type.badgeType === "neutral"
                    ? styles.badgeNeutral
                    : styles.badgePrimary;

                return (
                  <div
                    key={type.value}
                    className={`${styles.entryTypeCard} ${
                      isSelected ? styles.entryTypeCardActive : ""
                    }`}
                    onClick={() => {
                      setVisitType(type.value);
                    }}
                  >
                    <div
                      className={`${styles.entryTypeIconWrap} ${
                        isSelected ? styles.entryTypeIconActive : ""
                      }`}
                    >
                      {type.icon}
                    </div>

                    <div className={styles.entryTypeInfo}>
                      <div className={styles.entryTypeTitleRow}>
                        <span className={styles.entryTypeTitle}>{type.title}</span>
                        {type.badge && (
                          <span className={`${styles.entryTypeBadge} ${badgeVariantClass}`}>
                            {type.badge}
                          </span>
                        )}
                      </div>
                      <span className={styles.entryTypeSubtitle}>{type.subtitle}</span>
                    </div>

                    <div
                      className={`${styles.entryTypeRadio} ${
                        isSelected ? styles.entryTypeRadioActive : ""
                      }`}
                    >
                      {isSelected && <div className={styles.entryTypeRadioDot} />}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.stepNavigationFooter}>
              <Button
                type="button"
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => setCurrentStep(2)}
                trailIcon={<ArrowRight size={18} />}
              >
                Continue to Basic Details
              </Button>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* STEP 2: BASIC DETAILS (Name, Number, Photo)               */}
        {/* ========================================================= */}
        {currentStep === 2 && (
          <div className={styles.stepCard}>
            <div className={styles.stepHeader}>
              <div className={styles.stepHeaderIcon}>
                <User size={20} />
              </div>
              <div>
                <h2 className={styles.stepHeaderTitle}>
                  {STEP_2_HEADINGS[visitType]?.title || "Your Basic Details"}
                </h2>
                <p className={styles.stepHeaderSub}>
                  {STEP_2_HEADINGS[visitType]?.sub ||
                    "Enter your name, verify your mobile number, and take your security face photo."}
                </p>
              </div>
            </div>

            {/* 1. Mobile Number & Verification (Always top of Step 2, must verify first) */}
            {isOtpVerified ? (
              <div className={styles.verifiedPhoneCard}>
                <div className={styles.verifiedPhoneLeft}>
                  <div className={styles.verifiedCheckIcon}>
                    <Check size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <span className={styles.verifiedPhoneTitle}>Mobile Verified</span>
                    <span className={styles.verifiedPhoneValue}>+91 {mobileNumber}</span>
                  </div>
                </div>
                {!isDraftLocked && (
                  <button
                    type="button"
                    className={styles.changePhoneBtn}
                    onClick={() => {
                      setIsOtpVerified(false);
                      setOtpSent(false);
                      setOtpDigits(["", "", "", "", "", ""]);
                      setIsReturningVisitor(false);
                      setReturningData(null);
                      setShowPtmChanger(false);
                    }}
                  >
                    Change Number
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.formRow}>
                <FormField
                  label="Mobile Number"
                  required
                  helperText="We will send a 6-digit OTP on your mobile to verify your identity"
                >
                  <div className={styles.phoneInputRow}>
                    <div className={styles.phoneInputSlot}>
                      <TextInput
                        name="mobileNumber"
                        type="tel"
                        inputMode="numeric"
                        placeholder="Enter 10-digit mobile number"
                        prefixText="+91"
                        value={mobileNumber}
                        onChange={(e) =>
                          setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10))
                        }
                        maxLength={10}
                        autoFocus
                      />
                    </div>

                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleSendOtp}
                      loading={otpLoading && !otpSent}
                      disabled={mobileNumber.length !== 10 || resendCountdown > 0}
                      className={styles.sendOtpBtn}
                    >
                      {resendCountdown > 0 ? `${resendCountdown}s` : otpSent ? "Resend OTP" : "Get OTP"}
                    </Button>
                  </div>
                </FormField>

                {otpSent && (
                  <div className={styles.otpBoxesBlock}>
                    <label className={styles.otpBoxTitle}>
                      Enter the 6-digit OTP sent to your mobile <span className={styles.requiredStar}>*</span>
                    </label>
                    <div className={styles.otpBoxesRow} onPaste={handleOtpPaste}>
                      {otpDigits.map((digit, idx) => (
                        <input
                          key={idx}
                          ref={(el) => (otpInputRefs.current[idx] = el)}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={1}
                          className={`${styles.otpBoxInput} ${digit ? styles.otpBoxFilled : ""}`}
                          value={digit}
                          onChange={(e) => handleOtpDigitChange(idx, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                        />
                      ))}
                    </div>

                    <div className={styles.otpActionRow}>
                      <Button
                        type="button"
                        variant="primary"
                        size="lg"
                        fullWidth
                        onClick={handleVerifyOtp}
                        loading={otpLoading}
                        disabled={otpDigits.join("").length < 4}
                        trailIcon={<ArrowRight size={18} />}
                      >
                        Verify OTP Code
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CASE 1: OTP Verified AND Visitor is Recognized — Show ONLY the Recognized Section */}
            {isOtpVerified && isReturningVisitor && (
              <div className={styles.returningVisitorCard}>
                <div className={styles.vipPassRibbon}>
                  <Sparkles size={13} />
                  <span>Recognised Visitor • Fast-Track Entry</span>
                </div>

                <div className={styles.returningVisitorHeader}>
                  <div className={styles.returningAvatarWrapper}>
                    {returningData?.photo_url || selfieDataUrl ? (
                      <img
                        src={returningData?.photo_url || selfieDataUrl}
                        alt={fullName}
                        className={styles.returningAvatar}
                      />
                    ) : (
                      <div className={styles.returningAvatarFallback}>
                        {fullName?.charAt(0)?.toUpperCase() || "V"}
                      </div>
                    )}
                    <div className={styles.verifiedCheckBadge} title="Verified Visitor Identity">
                      <Check size={14} strokeWidth={3} />
                    </div>
                  </div>

                  <div className={styles.returningDetails}>
                    <h2 className={styles.returningTitle}>Welcome back, {fullName}!</h2>
                    <div className={styles.returningTagsRow}>
                      <span className={styles.verifiedTag}>
                        <ShieldCheck size={12} /> Photo on Record
                      </span>
                      <span className={styles.phoneTag}>
                        +91 {mobileNumber}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.returningLastVisitBox}>
                  <div className={styles.lastVisitItem}>
                    <span className={styles.lastVisitLabel}>Person to Meet</span>
                    <span className={styles.lastVisitValue}>
                      {selectedHostName ||
                        selectedHostObj?.name ||
                        selectedHostObj?.full_name ||
                        (selectedDeptObj ? `${selectedDeptObj.name} Department` : null) ||
                        returningData?.last_host_name ||
                        "General Team"}
                      {(selectedHostObj?.department_name || (!selectedHostObj && returningData?.last_department_name))
                        ? ` • ${selectedHostObj?.department_name || returningData.last_department_name}`
                        : ""}
                    </span>
                    <button
                      type="button"
                      className={styles.changePtmInlineBtn}
                      onClick={() => setShowPtmChanger((prev) => !prev)}
                    >
                      <User size={13} />
                      {showPtmChanger ? "Hide Selection" : "Change Person to Meet"}
                    </button>
                  </div>
                  {(vehicleNumber || returningData?.vehicle_number) && (
                    <div className={styles.lastVisitItem}>
                      <span className={styles.lastVisitLabel}>Registered Vehicle</span>
                      <span className={styles.lastVisitValue}>{vehicleNumber || returningData?.vehicle_number}</span>
                    </div>
                  )}
                </div>

                {/* Option to Change PTM inline */}
                {showPtmChanger && (
                  <div className={styles.returningPtmChangerSection}>
                    <div className={styles.returningPtmChangerHeader}>
                      <span className={styles.returningPtmChangerTitle}>Select Person to Meet or Department</span>
                      <button
                        type="button"
                        className={styles.closePtmChangerBtn}
                        onClick={() => setShowPtmChanger(false)}
                      >
                        <X size={14} /> Done
                      </button>
                    </div>
                    {renderDestinationSelector()}
                  </div>
                )}

                {/* Adding reason to meet is MANDATORY */}
                <div className={styles.returningPurposeSection}>
                  <div className={styles.returningPurposeHeader}>
                    <FileText size={18} />
                    <span className={styles.returningPurposeTitle}>
                      Reason / Purpose to Meet Today <span className={styles.requiredStar}>*</span>
                    </span>
                  </div>
                  <TextInput
                    name="purpose"
                    placeholder="Enter why you are meeting today (e.g. Official Discussion, Follow-up, Meeting)"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    leadIcon={<FileText size={16} />}
                    required
                  />
                  <div className={styles.chipsRow} style={{ marginTop: 8 }}>
                    {(PURPOSE_BY_TYPE[visitType] || PURPOSE_BY_TYPE.VISITOR).map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={`${styles.pillChip} ${
                          purpose === item ? styles.activePillChip : ""
                        }`}
                        onClick={() => setPurpose(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>

                <div className={styles.returningActions}>
                  <button
                    type="button"
                    className={styles.revisitBtn}
                    onClick={(e) => {
                      if (!purpose.trim()) {
                        toast.warn("Please enter or select the reason to meet today");
                        return;
                      }
                      handleSubmit(
                        e,
                        hostUserId || returningData?.last_host_id,
                        departmentId || returningData?.last_department_id
                      );
                    }}
                    disabled={submitting}
                  >
                    <Sparkles size={18} />
                    <div className={styles.revisitTextWrap}>
                      <span className={styles.revisitMainText}>
                        {submitting
                          ? "Generating Gate Pass..."
                          : `Confirm Entry: Meet ${
                              selectedHostName ||
                              selectedHostObj?.name ||
                              returningData?.last_host_name ||
                              "Host"
                            }`}
                      </span>
                      <span className={styles.revisitSubText}>
                        Instant pass using your verified profile and today&apos;s visit reason
                      </span>
                    </div>
                    <ArrowRight size={20} />
                  </button>
                </div>
              </div>
            )}

            {/* CASE 2: OTP Verified AND Visitor is First-Time / Not Recognized */}
            {isOtpVerified && !isReturningVisitor && (
              <>
                {/* Full Name */}
                <div className={styles.formRow}>
                  <FormField
                    label={STEP_2_HEADINGS[visitType]?.nameLabel || "Your Full Name"}
                    required
                  >
                    <TextInput
                      name="fullName"
                      placeholder={
                        STEP_2_HEADINGS[visitType]?.namePlaceholder ||
                        "Enter your full name (as per ID proof)"
                      }
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      leadIcon={<User size={16} />}
                      disabled={isDraftLocked}
                      required
                      autoFocus={!fullName}
                    />
                  </FormField>
                </div>

                {/* Live Camera Photo Capture */}
                {visitType !== "DELIVERY" && (
                  <div className={styles.formRow}>
                    <FormField
                      label="Live Security Photo (Mandatory Face Capture)"
                      required
                      helperText="Please look straight into the camera for a clear face photo. Required for visitor gate pass."
                    >
                      <FileUpload
                        value={selfieDataUrl}
                        onChange={(_, dataUrl) => {
                          setSelfieDataUrl(dataUrl);
                        }}
                        enableLiveCamera={true}
                        requireFaceDetection={true}
                        liveOnly={true}
                        label="Take live security photo"
                        helperText="Live camera front-facing photo with face detection"
                      />
                    </FormField>
                  </div>
                )}

                {/* Conditional: Delivery Company & Order ID */}
                {visitType === "DELIVERY" && (
                  <>
                    <div className={styles.formRow}>
                      <FormField label="Delivery Company / App" required helperText="Select your delivery app below or type company name">
                        <TextInput
                          name="deliveryCompany"
                          placeholder="e.g. Swiggy, Zomato, Amazon, Blinkit"
                          value={deliveryCompany}
                          onChange={(e) => setDeliveryCompany(e.target.value)}
                          leadIcon={<Package size={16} />}
                          required
                        />
                        <div className={styles.chipsRow}>
                          {DELIVERY_COMPANIES.map((brand) => (
                            <button
                              key={brand}
                              type="button"
                              className={`${styles.pillChip} ${
                                deliveryCompany === brand ? styles.activePillChip : ""
                              }`}
                              onClick={() => setDeliveryCompany(brand)}
                            >
                              {brand}
                            </button>
                          ))}
                        </div>
                      </FormField>
                    </div>

                    <div className={styles.formRow}>
                      <FormField label="Order / Parcel Tracking Number (Optional)" optionalLabel>
                        <TextInput
                          name="orderId"
                          placeholder="e.g. 84920 or Order ID"
                          value={orderId}
                          onChange={(e) => setOrderId(e.target.value)}
                          leadIcon={<Hash size={16} />}
                        />
                      </FormField>
                    </div>
                  </>
                )}

                {/* Vehicle Number Plate & Transport Details */}
                {visitType !== "DELIVERY" && (
                  <div className={styles.formRow}>
                    <FormField
                      label={
                        visitType === "VEHICLE"
                          ? "Vehicle Registration Number"
                          : "Vehicle Registration Number (Optional)"
                      }
                      required={visitType === "VEHICLE"}
                      optionalLabel={visitType !== "VEHICLE"}
                      helperText={
                        visitType === "VEHICLE"
                          ? "Enter vehicle registration number (e.g. MH 02 AB 1234) for gate access & parking pass"
                          : "Arrived by vehicle? Enter registration number for society parking / gate clearance"
                      }
                    >
                      <div className={styles.hsrpPlateWrapper}>
                        {useSinglePlateInput ? (
                          <TextInput
                            name="vehicleNumber"
                            placeholder="e.g. MH 02 AB 1234"
                            value={vehicleNumber}
                            onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                            leadIcon={<Car size={16} />}
                            disabled={isDraftLocked}
                            required={visitType === "VEHICLE"}
                          />
                        ) : (
                          <div className={styles.hsrpPlateContainer}>
                            <div
                              className={styles.hsrpIndStripe}
                              title="India High Security Registration Plate"
                            >
                              <div className={styles.hsrpIndChakra} />
                              <span className={styles.hsrpIndText}>IND</span>
                            </div>

                            <div className={styles.hsrpBoxesRow} onPaste={handlePlatePaste}>
                              <div className={styles.hsrpBoxCol}>
                                <input
                                  ref={plateStateRef}
                                  type="text"
                                  maxLength={2}
                                  placeholder="MH"
                                  className={`${styles.hsrpBoxInput} ${
                                    plateState ? styles.hsrpBoxInputFilled : ""
                                  }`}
                                  value={plateState}
                                  onChange={(e) => handlePlateStateChange(e.target.value)}
                                  disabled={isDraftLocked}
                                  aria-label="State Code"
                                />
                                <span className={styles.hsrpBoxLabel}>State</span>
                              </div>

                              <span className={styles.hsrpDivider}>•</span>

                              <div className={styles.hsrpBoxCol}>
                                <input
                                  ref={plateRtoRef}
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={2}
                                  placeholder="02"
                                  className={`${styles.hsrpBoxInput} ${
                                    plateRto ? styles.hsrpBoxInputFilled : ""
                                  }`}
                                  value={plateRto}
                                  onChange={(e) => handlePlateRtoChange(e.target.value)}
                                  onKeyDown={(e) => handlePlateKeyDown("rto", e)}
                                  disabled={isDraftLocked}
                                  aria-label="RTO District Code"
                                />
                                <span className={styles.hsrpBoxLabel}>RTO</span>
                              </div>

                              <span className={styles.hsrpDivider}>•</span>

                              <div className={styles.hsrpBoxCol}>
                                <input
                                  ref={plateSeriesRef}
                                  type="text"
                                  maxLength={3}
                                  placeholder="AB"
                                  className={`${styles.hsrpBoxInput} ${
                                    plateSeries ? styles.hsrpBoxInputFilled : ""
                                  }`}
                                  value={plateSeries}
                                  onChange={(e) => handlePlateSeriesChange(e.target.value)}
                                  onKeyDown={(e) => handlePlateKeyDown("series", e)}
                                  disabled={isDraftLocked}
                                  aria-label="Series Code"
                                />
                                <span className={styles.hsrpBoxLabel}>Series</span>
                              </div>

                              <span className={styles.hsrpDivider}>•</span>

                              <div className={styles.hsrpBoxCol}>
                                <input
                                  ref={plateNumberRef}
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={4}
                                  placeholder="1234"
                                  className={`${styles.hsrpBoxInput} ${
                                    plateNumber ? styles.hsrpBoxInputFilled : ""
                                  }`}
                                  value={plateNumber}
                                  onChange={(e) => handlePlateNumberChange(e.target.value)}
                                  onKeyDown={(e) => handlePlateKeyDown("number", e)}
                                  disabled={isDraftLocked}
                                  aria-label="Unique Vehicle Number"
                                />
                                <span className={styles.hsrpBoxLabel}>Number</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className={styles.hsrpToggleRow}>
                          <button
                            type="button"
                            className={styles.hsrpToggleBtn}
                            onClick={() => setUseSinglePlateInput(!useSinglePlateInput)}
                          >
                            {useSinglePlateInput
                              ? "Switch to Indian Plate Boxes"
                              : "Switch to Single Text Box"}
                          </button>

                          {vehicleNumber && (
                            <div className={styles.hsrpValuePreview}>
                              Vehicle: <strong>{vehicleNumber}</strong>
                              {" • "}
                              <button
                                type="button"
                                className={styles.hsrpClearBtn}
                                onClick={handleClearPlate}
                                disabled={isDraftLocked}
                              >
                                Clear
                              </button>
                            </div>
                          )}
                        </div>

                        <div className={styles.chipsRow}>
                          {(visitType === "VEHICLE"
                            ? CAB_COMPANIES
                            : VISITOR_VEHICLE_TYPES
                          ).map((cab) => (
                            <button
                              key={cab}
                              type="button"
                              className={`${styles.pillChip} ${
                                deliveryCompany === cab ? styles.activePillChip : ""
                              }`}
                              onClick={() =>
                                setDeliveryCompany(deliveryCompany === cab ? "" : cab)
                              }
                            >
                              {cab}
                            </button>
                          ))}
                        </div>
                      </div>
                    </FormField>
                  </div>
                )}
              </>
            )}

            <div className={styles.stepNavigationFooter}>
              <Button
                type="button"
                variant="secondary"
                size="lg"
                className={styles.stepNavBackBtn}
                onClick={() => setCurrentStep(1)}
                icon={<ArrowLeft size={18} />}
              >
                Back
              </Button>
              {isOtpVerified && !isReturningVisitor && (
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  className={styles.stepNavNextBtn}
                  onClick={handleProceedToStep3}
                  trailIcon={<ArrowRight size={18} />}
                >
                  Next: Visit Details
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* STEP 3: VISIT DETAILS (Whom to Meet, Persons, Purpose)    */}
        {/* ========================================================= */}
        {currentStep === 3 && (
          <form className={styles.mainCheckinForm} onSubmit={handleSubmit}>
            <div className={styles.stepCard}>
              <div className={styles.stepHeader}>
                <div className={styles.stepHeaderIcon}>
                  <FileText size={20} />
                </div>
                <div>
                  <h2 className={styles.stepHeaderTitle}>
                    {STEP_3_HEADINGS[visitType]?.title || "Visit Information"}
                  </h2>
                  <p className={styles.stepHeaderSub}>
                    {STEP_3_HEADINGS[visitType]?.sub ||
                      "Whom are you visiting today, total persons in your group, and purpose of visit."}
                  </p>
                </div>
              </div>

              {/* 1. Whom to Meet */}
              <div className={styles.formRow}>
                {renderDestinationSelector()}
              </div>

              {/* 2. Total Persons Visiting (Hidden for Delivery - always 1) */}
              {visitType !== "DELIVERY" && (
                <div className={styles.formRow}>
                  <FormField label="Total Persons Visiting (Including You)" required>
                    <div className={styles.stepperWrap}>
                      <button
                        type="button"
                        className={styles.stepperBtn}
                        onClick={() => setNoOfVisitors((prev) => Math.max(1, Number(prev) - 1))}
                        disabled={Number(noOfVisitors) <= 1}
                        aria-label="Decrease visitor count"
                      >
                        -
                      </button>
                      <span className={styles.stepperValue}>
                        {noOfVisitors} {Number(noOfVisitors) === 1 ? "Person" : "Persons"}
                      </span>
                      <button
                        type="button"
                        className={styles.stepperBtn}
                        onClick={() => setNoOfVisitors((prev) => Math.min(20, Number(prev) + 1))}
                        aria-label="Increase visitor count"
                      >
                        +
                      </button>
                    </div>
                  </FormField>
                </div>
              )}

              {/* 3. Purpose of Visit (Auto-assigned note for delivery) */}
              {visitType !== "DELIVERY" ? (
                <div className={styles.formRow}>
                  <FormField label="Purpose of Visit" required>
                    <TextInput
                      name="purpose"
                      placeholder={
                        visitType === "VENDOR"
                          ? "e.g. Maintenance & Repair, Vendor Work"
                          : "e.g. Official Meeting, Interview, Personal Visit"
                      }
                      value={purpose}
                      onChange={(e) => setPurpose(e.target.value)}
                      leadIcon={<FileText size={16} />}
                      required
                    />
                    <div className={styles.chipsRow}>
                      {(PURPOSE_BY_TYPE[visitType] || PURPOSE_BY_TYPE.VISITOR).map((item) => (
                        <button
                          key={item}
                          type="button"
                          className={`${styles.pillChip} ${
                            purpose === item ? styles.activePillChip : ""
                          }`}
                          onClick={() => setPurpose(item)}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </FormField>
                </div>
              ) : (
                <div className={styles.formRow}>
                  <div className={styles.deliveryPurposeNote}>
                    <div className={styles.deliveryPurposeIcon}>
                      <Package size={18} />
                    </div>
                    <div>
                      <span className={styles.deliveryPurposeTitle}>
                        Purpose: Delivery / Courier Drop
                      </span>
                      <p className={styles.deliveryPurposeSub}>
                        Auto-assigned for rapid delivery gate entry
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 4. DPDP Consent Checkbox */}
              <div className={styles.formRow}>
                <div className={styles.consentContainer}>
                  <Checkbox
                    name="dpdpConsent"
                    checked={dpdpConsent}
                    onChange={(e) => setDpdpConsent(e.target.checked)}
                    label="Consent for Visitor Verification (DPDP Act)"
                    description={`I agree to provide my mobile number and photo strictly for visitor safety, identity verification, and entry logging at ${propertyName || "AsmitA"} premises.`}
                    required
                  />
                </div>
              </div>

              <div className={styles.stepNavigationFooter}>
                <Button
                  type="button"
                  variant="secondary"
                  size="lg"
                  className={styles.stepNavBackBtn}
                  onClick={() => setCurrentStep(2)}
                  icon={<ArrowLeft size={18} />}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className={styles.stepNavNextBtn}
                  loading={submitting}
                  trailIcon={<ArrowRight size={18} />}
                >
                  Generate Digital Entry Pass
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
